/**
 * Client portal Tasks (DD-053): the client's client-visible tasks under
 * "Da fare / Fatte" tabs, with the shared rows and drawer in client mode
 * (DD-017, DD-051). A client-role session edits (`canEdit`), every other
 * session reads. A save that hands the task to the team closes the drawer
 * with a toast, and the row leaves the list. The page title reaches the
 * header trail through ApprovalView (DD-025).
 */

import React, { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { PanelFilterOption, PanelFilterTabs } from "@/components/ui/PanelFilterTabs";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { useAppStore } from "@/core/store/useAppStore";
import { useLocale } from "@/hooks/useLocale";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { TaskListEditor } from "@/domains/Tasks/components/TaskListEditor";
import { TaskDrawer } from "@/domains/Tasks/drawer/TaskDrawer";
import { useTaskDrawer } from "@/domains/Tasks/drawer/useTaskDrawer";
import { useToggleTask } from "@/domains/Tasks/hooks/useTasks";
import { capabilitiesFor } from "@/domains/Tasks/taskCapabilities";
import type { Task, TaskList, TaskStatus } from "@/domains/Tasks/types";
import { cn } from "@/utils/cn";
import { usePortalTasksQuery } from "../hooks/usePortalTasks";

const NO_TASKS: Task[] = [];

const isTaskStatus = (id: string): id is TaskStatus => id === "open" || id === "done";

export const ClientTasksRoute: React.FC = () => {
  const { clientId } = useOutletContext<{ clientId: string }>();
  const { t } = useLocale();
  const addToast = useAppStore((store) => store.addToast);
  const [status, setStatus] = useState<TaskStatus>("open");
  const [collapsed, setCollapsed] = useState(false);
  const { data, isLoading, isError, isPlaceholderData } = usePortalTasksQuery(clientId, status);
  const toggleTask = useToggleTask();
  const drawer = useTaskDrawer();

  // Read-only until the list says this session edits.
  const canEdit = data?.canEdit ?? false;
  const capabilities = useMemo(() => capabilitiesFor(canEdit ? "client" : "client-viewer"), [canEdit]);
  const tasks = data?.tasks ?? NO_TASKS;
  const list = useMemo<TaskList>(() => ({ tasks, via: [] }), [tasks]);
  const tabs: PanelFilterOption[] = [
    { id: "open", label: t("client.tasks.open") },
    { id: "done", label: t("client.tasks.done") },
  ];
  const TaskEntityIcon = ENTITY_CONFIG.task.icon;

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="w-full max-w-2xl mx-auto px-4 lg:px-6 py-4">
        <WorkspacePanel
          title={t("client.tasks.title")}
          icon={<TaskEntityIcon className="w-4 h-4" />}
          subheader={
            <PanelFilterTabs
              options={tabs}
              activeId={status}
              onChange={(id) => {
                if (isTaskStatus(id)) setStatus(id);
              }}
            />
          }
          loading={isLoading}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((value) => !value)}
        >
          {isError ? (
            <p className="text-sm text-red-600">{t("client.tasks.loadError")}</p>
          ) : (
            <div
              className={cn("transition-opacity", isPlaceholderData && "opacity-60 pointer-events-none")}
              aria-busy={isPlaceholderData}
            >
              <TaskListEditor
                list={list}
                capabilities={capabilities}
                onToggle={toggleTask}
                onOpen={drawer.open}
                emptyHint={status === "open" ? t("client.tasks.emptyOpen") : t("client.tasks.emptyDone")}
              />
            </div>
          )}
        </WorkspacePanel>
      </div>
      <TaskDrawer
        drawer={drawer}
        capabilities={capabilities}
        onTeamOnly={() => addToast({ message: t("client.tasks.movedToTeam"), type: "info" })}
      />
    </div>
  );
};
